import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play } from "lucide-react";
import { FadeUp, Eyebrow } from "./Reveal";

const TABS = [
  {
    id: "ten",
    label: "10-Min Ignition",
    testid: "product-tab-strength",
    image: "https://static.prod-images.emergentagent.com/jobs/753a59ae-6295-4a2c-a309-76467ea4b06e/images/6a2e4d4385205d8c8be1258d55e0e975ba420153a6fa5a7c6d53b3ed16825481.png",
    metric: "HIIT • ~140 kcal",
    title: "The no-excuses minimum",
    copy: "High-intensity intervals compressed into ten ruthless minutes. Warm-up in 90 seconds, all-out work blocks, done before your coffee cools.",
  },
  {
    id: "twenty",
    label: "20-Min Builder",
    testid: "product-tab-biometrics",
    image: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=1200&q=80",
    metric: "Strength • 26 Sets",
    title: "Strength on a lunch break",
    copy: "Supersets and auto-trimmed rest windows pack a full strength session into twenty minutes — no filler, no standing around.",
  },
  {
    id: "thirty",
    label: "30-Min Burn",
    testid: "product-tab-recovery",
    image: "https://images.unsplash.com/photo-1518611012118-696072aa579a?auto=format&fit=crop&w=1200&q=80",
    metric: "Hybrid • Zone 3",
    title: "The everyday sweet spot",
    copy: "Strength and conditioning blended into one half-hour block. The session most members build their entire week around.",
  },
  {
    id: "forty",
    label: "40-Min Grind",
    testid: "product-tab-grind",
    image: "https://images.unsplash.com/photo-1517838277536-f5f99be501cd?auto=format&fit=crop&w=1200&q=80",
    metric: "Power • 2 Cycles",
    title: "The serious hour's better alternative",
    copy: "Full warm-up, two work-block cycles, active rest, and a cooldown that earns it. Forty minutes of structured effort built for people who push.",
  },
  {
    id: "fifty",
    label: "50-Min Full Send",
    testid: "product-tab-nutrition",
    image: "https://images.unsplash.com/photo-1581009146145-b5ef050c2e1e?auto=format&fit=crop&w=1200&q=80",
    metric: "Full • Max Volume",
    title: "The complete session",
    copy: "Fifty programmed minutes: extended warm-up, full work capacity, finisher, and a guided cooldown. The most Fit50 can give you in one go.",
  },
];

export const ProductShowcase = () => {
  const [active, setActive] = useState(TABS[0]);

  return (
    <section id="showcase" data-testid="product-showcase-section" className="relative py-32 bg-carbon scroll-mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeUp>
          <Eyebrow>Inside The App</Eyebrow>
          <h2 className="font-display font-extrabold uppercase tracking-tight leading-tight text-3xl sm:text-4xl lg:text-5xl mt-4 max-w-3xl">
            One app. Five durations. <span className="text-volt">Infinite schedules.</span>
          </h2>
        </FadeUp>

        <FadeUp delay={0.15}>
          <div className="mt-12 flex flex-wrap gap-2">
            {TABS.map((t) => (
              <button
                key={t.id}
                data-testid={t.testid}
                onClick={() => setActive(t)}
                className={`font-mono text-xs tracking-[0.2em] uppercase px-5 py-3 border transition-colors duration-200 ${
                  active.id === t.id
                    ? "bg-volt text-white border-volt font-semibold"
                    : "border-white/15 text-neutral-400 hover:text-volt hover:border-volt/50"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </FadeUp>

        <div className="mt-10 grid lg:grid-cols-12 gap-8 items-stretch">
          <div className="lg:col-span-7 relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                initial={{ opacity: 0, scale: 0.97, clipPath: "inset(6% 6% 6% 6%)" }}
                animate={{ opacity: 1, scale: 1, clipPath: "inset(0% 0% 0% 0%)" }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="relative clip-corner overflow-hidden border border-white/15 h-[380px] sm:h-[480px]"
              >
                <img
                  src={active.image}
                  alt={active.label}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/30 to-transparent" />
                <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 20%, rgba(255,107,0,0.10) 0%, transparent 60%)" }} />
                <div className="absolute bottom-6 left-6 bg-obsidian/85 backdrop-blur-md border border-volt/40 px-5 py-3">
                  <span className="font-mono text-[10px] tracking-widest uppercase text-neutral-400 block">Live Metric</span>
                  <span className="font-display font-black text-2xl text-volt uppercase">{active.metric}</span>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="lg:col-span-5 flex">
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                className="flex flex-col justify-center border border-white/10 bg-carbon p-8 sm:p-10 w-full"
              >
                <span className="font-mono text-xs tracking-[0.25em] uppercase text-volt">{active.label}</span>
                <h3 className="font-display font-extrabold uppercase text-2xl sm:text-3xl mt-4 leading-tight">
                  {active.title}
                </h3>
                <p className="mt-5 text-base sm:text-lg text-neutral-400 leading-relaxed">{active.copy}</p>
                <button
                  data-testid="showcase-watch-session-button"
                  onClick={() => window.dispatchEvent(new Event("open-fit50-demo"))}
                  className="mt-8 inline-flex w-fit items-center gap-2 border border-white/25 px-6 py-3 font-display font-bold uppercase tracking-wide text-neutral-100 hover:border-volt hover:text-volt transition-colors duration-200"
                >
                  <Play className="h-4 w-4 fill-current" />
                  Watch A Session
                </button>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
